import React, { useRef } from "react";
import { Typography } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import Divider from "@mui/material/Divider";
import "../JaldiLab.css";
import {
  getCircleTypeListItem,
  getTextContentWithHeading,
  referenceHtml,
  referenceTootKit,
  h1style,
  h2style,
  h3style,
  h4style,
  getTextContentHeading,
  paraStyle,
  DividerBorderStyle,
  DashedBorderStyle,
  referenceStyle,
  subHeadingStyle,
  sectionStyle,
  subHeadingContent,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import scrunityImg from "../../../assets/Images/filingSection Image/scrunity.png";
import filingCounterImg from "../../../assets/Images/filingSection Image/filingCounter.png";
import electronicSystem from "../../../assets/Images/filingSection Image/electronicSystem.jpeg";
import { FigureDesc50, ImageDesc60, ImageDesc61 } from "../../../utils/tmp/ImageDescription/FilingSectionID";

const FilingSection = ({ goToSection }) => {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content: (
        <>
          Supreme Court of India,{" "}
          <i>
            Supreme Court of India: Handbook on Practice and Procedure and
            Office Procedure
          </i>{" "}
          (2017) pp. 78-97.
        </>
      ),
    },
    {
      content:
        "Supreme Court of India, ‘Practice Directions for E-Filing (Phase-II) in The Supreme Court of India’",
      link: "https://main.sci.gov.in/php/FAQ/5_6246991526434439183.pdf",
      description:
        " <https://main.sci.gov.in/php/FAQ/5_6246991526434439183.pdf> [“e-Filing Practice Guidelines”]",
    },
  ];

  const filingAreaOject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Design the counters for{" "}
        <span style={{ color: "#DC002D" }}>
          ease of access for litigants and lawyers
        </span>
        ;
      </>,
      <>
        Equip the filing counter with provisions for{" "}
        <span style={{ color: "#DC002D" }}>physical as well as e-filing</span>;
      </>,
      <>
        Promote efficient scrutiny by providing{" "}
        <span style={{ color: "#DC002D" }}>
          adequate space for organising functions;
        </span>{" "}
        and
      </>,
      <>
        Ensure{" "}
        <span style={{ color: "#DC002D" }}>
          adequate space is provided for storage{" "}
        </span>{" "}
        of files, both physical and digital, as required.{" "}
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Filing Officers",
      "Scrutiny Officers",
      "Advocates",
      "Litigants/members of the public ",
    ],
    isOrdered: false,
    isImage: true,
  };

  const nestedListItem = [
    "An external counter for the public/litigants and an internal counter for staff; and",
    "A secure entry to the private sections of the space, where only Court staff are permitted.",
  ];

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The filing section must be ideally located on the ground floor of the Court Complex, preferably near the entrance lobby.",

      "The scrutiny branch must be located within the same space as the filing section, with adequate separate space being provided.",

      "The copying section, e-Seva Kendra, and bank/ATM facilities must be provided in proximity to the filing section.",

      <>
        The filing section must have:{" "}
        {getCircleTypeListItem(nestedListItem, true)}
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const designConsiderationsObject = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations ",
      items: [
        "Adequate storage space must be provided, for physical files and documents and personal belongings of the Court staff.",
        "Printers, photocopying/scanning and other reprographic facilities must be provided, to print hard copies of documents for physical filing. ",
        "Provision must be made for concealed wiring for computer and printer connections to facilitate uninterrupted movement within the space.",
        "The design of the space must be optimised to facilitate smooth movement of users to avoid overcrowding.",
      ],
    },
    {
      heading: "Filing Counter",
      items: [
        <>
          A user-friendly and accessible filing counter must be provided, to
          facilitate ease of filing documents manually for litigants and
          lawyers.
          {reference(referenceData[0], 1)}
        </>,

        <>
          An accessible e-kiosk/electronic system and computer facilities,
          including use of USB drives/other external storage devices, must be
          provided to facilitate easy digital filing for litigants/lawyers.
          {reference(referenceData[1], 2)}
        </>,

        "Ergonomic furniture must be provided for staff as well as the Advocates and litigants.",

        "Provision must be made to allow for digital authentication of Affidavits, Vakalatnamas, and other paper documents with physical signatures through suitable biometric devices.",

        "Provision must be made for online payment of Court fees, process fees, etc., as required, and for generation of payment receipts. ",
      ],
    },
    {
      heading: "Scrutiny Branch",
      items: [
        "Ergonomic work desks with chairs must be provided for the use of Scrutiny Officers.",

        "Computers and electronic systems with adequate, high-speed Internet connections must be provided, to conduct online scrutiny. ",
      ],
    },
  ];

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 style={h3style}>
          <span style={{ fontSize: "20px" }}>2.5. </span>
          Administrative Wing
        </h3>
        <h4 id="headingFilingSection" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.5.</span>A. Administrative
          Offices with Public Interfaces
        </h4>
        <h3 id="filingBranch" style={subHeadingStyle}>
          I. Filing Section and Scrutiny Branch
        </h3>
        <p style={paraStyle}>
          The first point of contact for litigants and Advocates upon entering a
          Court Complex is the filing section, where fresh cases are submitted
          to the Filing Officers. Upon receiving the plaint and other documents,
          including the payment of the Court fees, vakalatnama, and other
          requirements, the file is then passed to a Scrutiny Officer at the
          scrutiny branch. Scrutiny Officers verify the file to ensure that
          there are no defects or objections to be found. Once scrutiny has been
          completed, the file then moves to the pending branch of the Courtroom
          to which it has been allotted.
        </p>
        <p style={paraStyle}>
          The Filing Section and Scrutiny Branch are closely interlinked, and
          are public-facing in their use due to the need for constant
          communication with lawyers, although the act of scrutiny itself
          requires a private space for uninterrupted work. Provision must
          therefore be made for both these public and private functions.
        </p>
        <p style={paraStyle}>
          The filing section and scrutiny branch have a
          front-of-the-house/back-of-the-house layout.
        </p>
        {getTextContentHeading(filingAreaOject)}
        {getTextContentHeading(usersObject)}
        {getTextContentHeading(adjacenciesObject)}
        {subHeadingContent(designConsiderationsObject)}
        <CaptionWithBorders1
          image={[scrunityImg]}
          isZoom={true}
          caption={
            "Figure 50: Typical floor plan for the filing section and scrutiny branch "
          }
          zoomImgStyle={{ height: "600px" }}
          imageDescription={FigureDesc50}
        />
        <CaptionWithBorders1
          image={[filingCounterImg]}
          caption={
            "Image 60: Examples of accessible kiosk heights at the filing counter "
          }
          creditLabel={"Source"}
          imageCredit={"Harmonised Guidelines 2020"}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc60}
        />
        <CaptionWithBorders1
          image={[electronicSystem]}
          caption={"Image 61: Accessible e-kiosk/electronic system"}
          creditLabel={"Image Credit"}
          imageCredit={"369 Ochre Studio"}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={ImageDesc61}
        />
        <DividerBorderStyle />
        <h3 id="listingBranch" style={subHeadingStyle}>
          II. Listing Branch
        </h3>
        <p style={{ paraStyle }}>
          The listing branch manages the scheduling and listing of Court cases.
          Lawyers may utilise the listing branch to ascertain details about the
          causelists and the status of specific cases before specific
          Courtrooms. Therefore, the listing branch has certain public-facing
          functions.
        </p>
        <DividerBorderStyle />
        <h3 id="copyingBranch" style={subHeadingStyle}>
          III. Copying/Dispatch Branch
        </h3>
        <p style={paraStyle}>
          The copying branch contains facilities required to make certified
          copies of case files and orders/decrees passed by the Court, and must
          be located in close proximity to the filing section and the Pending
          Branch and Record Room (
          <span
            style={sectionStyle}
            onClick={() => goToSection("", "HeadingAnnexure1")}
          >
            Section 2.5.D
          </span>
          ). This function is usually combined with the dispatch branch, which
          is tasked with overseeing communication between various administrative
          wings of the Court itself as well as handling external communication
          with other Courts.
        </p>
        <p style={paraStyle}>
          Photocopy machines and other printing facilities, as well as
          provisions for signing and verifying copies, must be provided at the
          copying branch. Additionally, electronic copying facilities and
          digital signatures must be provided.
        </p>
        <p style={paraStyle}>
          The dispatch branch must have adequate space for the sorting and
          distribution of any physical post or notices that may be received.
          Additionally, online communication facilities, with adequate IT
          support infrastructure, must be provided.
        </p>
        <p style={paraStyle}>
          The copying/dispatch branch may have a
          front-of-the-house/back-of-the-house layout.
        </p>
        <h2 ref={referencesRef} id="filingSectionRef" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default FilingSection;
